import type {
  ChatMessageItem,
  ChatMessageToolCall,
  ChatStreamContentRaw,
  ClawContent,
  ClawMessage,
  ClawSendFile,
} from '../types';
import { parseYoloSegmentPayload, YOLO_SEGMENT_CONTENT_TYPE } from './story-payload';

export type { PendingAttachmentReplacement } from './pending-attachment-content';
export {
  buildClawMessageContentsFromInputValue,
  stringifyTextPromptWithPendingAttachmentReplacements,
} from './pending-attachment-content';

interface ParseToolCallsFromContentsOptions {
  fallbackIdPrefix: string;
  extractOutput?: (content: ChatStreamContentRaw) => string | undefined;
}

interface ExtractMessageTextOptions {
  failedFallbackMessage?: string;
}

const ATTACHMENT_CONTENT_TYPES = ['image', 'video', 'audio', 'file', 'markdown'] as const;

/** 把 ChatInput 发送的草稿附件转成 ChatMessageItem.fileList 用的 ClawContent。
 *  统一从这里出口，避免 user 消息（手写 { content, type }）和历史消息（来自 extractAttachmentContents）字段命名错位。 */
export const toAttachmentContent = (file: ClawSendFile): ClawContent => ({
  type: file.file_type,
  content: file.file_path,
});

const buildAttachmentContentKey = ({ type, content }: { type?: string; content?: string }) =>
  `${type ?? ''}:${content ?? ''}`;

const mergeMessageIds = (source: string[] = [], incoming: string[] = []): string[] => {
  return Array.from(new Set([...source, ...incoming]));
};

const mergeStreamSeqs = (source?: string[], incoming?: string[]): string[] | undefined => {
  const merged = Array.from(new Set([...(source ?? []), ...(incoming ?? [])]));
  return merged.length ? merged : undefined;
};

const extractAttachmentContents = <T extends { type?: string; content?: string }>(contents: T[]): T[] => {
  return contents.filter(
    (content): content is T =>
      ATTACHMENT_CONTENT_TYPES.includes((content.type ?? '') as (typeof ATTACHMENT_CONTENT_TYPES)[number]) &&
      !!content.content,
  );
};

export const mergeAttachmentContentsByTypeAndContent = <T extends { type?: string; content?: string }>(
  source: T[],
  incoming: T[],
): T[] => {
  if (!incoming.length) {
    return source;
  }

  const merged = [...source];
  const keyToIndexMap = new Map<string, number>();

  merged.forEach((item, index) => {
    keyToIndexMap.set(buildAttachmentContentKey(item), index);
  });

  for (const item of incoming) {
    const key = buildAttachmentContentKey(item);
    const existedIndex = keyToIndexMap.get(key);
    if (existedIndex === undefined) {
      keyToIndexMap.set(key, merged.length);
      merged.push(item);
      continue;
    }

    merged[existedIndex] = {
      ...merged[existedIndex],
      ...item,
    };
  }

  return merged;
};

const parseToolCallsFromContents = (
  contents: ChatStreamContentRaw[],
  { fallbackIdPrefix, extractOutput }: ParseToolCallsFromContentsOptions,
): ChatMessageToolCall[] => {
  return contents.flatMap((content, contentIndex) => {
    const normalizedOutput = extractOutput?.(content)?.trim();

    return (content.tool_calls ?? []).map((tool, toolIndex) => ({
      id: tool.id || `${fallbackIdPrefix}-${contentIndex}-${toolIndex}`,
      type: tool.type,
      name: tool.function?.name?.trim() || 'unknown-tool',
      arguments: tool.function?.arguments?.trim() || '',
      output: normalizedOutput || undefined,
      thoughtSignature: tool.function?.thought_signature?.trim(),
    }));
  });
};

export const mergeToolCallsById = (
  source: ChatMessageToolCall[],
  incoming: ChatMessageToolCall[],
): ChatMessageToolCall[] => {
  const merged = [...source];

  for (const nextTool of incoming) {
    const existedIndex = merged.findIndex((tool) => tool.id === nextTool.id);
    if (existedIndex < 0) {
      merged.push(nextTool);
      continue;
    }

    const existedTool = merged[existedIndex];
    merged[existedIndex] = {
      ...existedTool,
      ...nextTool,
      status: nextTool.status || existedTool.status,
      arguments: nextTool.arguments || existedTool.arguments,
      output: [existedTool.output, nextTool.output].filter(Boolean).join('\n').trim() || undefined,
      thoughtSignature: nextTool.thoughtSignature || existedTool.thoughtSignature,
    };
  }

  return merged;
};

export const extractMessageText = (
  message: ClawMessage,
  { failedFallbackMessage = '' }: ExtractMessageTextOptions = {},
): ChatMessageItem | null => {
  const contents = message.contents ?? [];
  const attachmentContents = extractAttachmentContents(contents);

  const textContent = contents
    .filter(
      (content) =>
        content.type === 'text' &&
        content.status !== 'analysis' &&
        // turn.failed 落地的文本是 JSON 错误体（status==='failed'），不能当正文渲染，
        // 交由 extractFailedStateMessage 兜成本地化的失败提示。
        content.status !== 'failed' &&
        !!content.content,
    )
    .map((content) => content.content?.trim() ?? '')
    .join('\n')
    .trim();

  if (message.role === 'user') {
    if (!textContent && attachmentContents.length === 0) return null;

    return {
      id: message.id ? `user-${message.id}` : `user-${Date.now()}`,
      serverMessageId: message.id,
      shareMessageIds: message.id ? [message.id] : undefined,
      role: 'user',
      // 纯文件 user 消息（无文本）时，与实时发送（use-chat-scene.ts handleSend）的兜底对齐，
      // 用 `[type] path` 回填 content，避免历史回放后气泡正文 / 锚点 label 出现空串。
      content:
        textContent ||
        attachmentContents
          .map((file) => `[${file.type ?? ''}] ${file.content ?? ''}`)
          .join('\n')
          .trim(),
      fileList: attachmentContents,
      createdAt: message.created_at,
    };
  }

  if (message.role === 'assistant' || message.role === 'tool') {
    // cron/spawn 异步任务产生的 tool 记录（memory、message、cron tick 等）turn_id 为空，
    // 不需要展示为 tool 卡片——用户只关心 cron/spawn 消息本身，内部工具调用是噪音。
    // 普通 AI turn 里的工具调用 turn_id 非空，正常保留。
    if (message.role === 'tool' && !message.turn_id) return null;
    const thinking = contents
      .flatMap((content) => {
        const thinkingParts: string[] = [];

        const reasoningContent = content.reasoning_content?.trim();
        if (reasoningContent) {
          thinkingParts.push(reasoningContent);
        }

        // 后端约定：status=analysis 的文本属于思考过程，不应进入最终回复正文
        if (content.status === 'analysis') {
          const analysisContent = content.content?.trim();
          if (analysisContent) {
            thinkingParts.push(analysisContent);
          }
        }

        return thinkingParts;
      })
      .filter(Boolean)
      .join('\n')
      .trim();

    const tool_calls = parseToolCallsFromContents(contents, {
      fallbackIdPrefix: message.id || message.role || 'assistant',
      extractOutput: message.role === 'tool' ? (content) => content.content : undefined,
    });
    const failedStateMessage =
      message.role === 'assistant' ? extractFailedStateMessage(contents, failedFallbackMessage) : '';

    const yoloSegmentRaw = contents.find((content) => content.type === YOLO_SEGMENT_CONTENT_TYPE)?.content;
    const story = yoloSegmentRaw ? parseYoloSegmentPayload(yoloSegmentRaw) : null;

    const assistantTextContent = message.role === 'assistant' ? textContent : '';
    const assistantThinking = message.role === 'assistant' ? thinking : '';
    const shareMessageIds =
      message.role === 'tool'
        ? []
        : message.id &&
            (assistantTextContent || assistantThinking || failedStateMessage || attachmentContents.length > 0 || story)
          ? [message.id]
          : undefined;
    if (
      !assistantTextContent &&
      !assistantThinking &&
      !failedStateMessage &&
      tool_calls.length === 0 &&
      attachmentContents.length === 0 &&
      !story
    ) {
      return null;
    }

    return {
      id: message.id ? `assistant-${message.id}` : `assistant-${Date.now()}-${message.role}`,
      serverMessageId: message.id,
      turnId: message.turn_id || undefined,
      streamSeqs: message.stream_seq ? [message.stream_seq] : undefined,
      shareMessageIds,
      role: 'assistant',
      content: assistantTextContent || failedStateMessage,
      thinking: assistantThinking || undefined,
      tool_calls: tool_calls.length ? tool_calls : undefined,
      createdAt: message.created_at,
      fileList: attachmentContents.length ? attachmentContents : undefined,
      story: story ?? undefined,
    };
  }

  if (message.role === 'spawn' || message.role === 'cron') {
    return {
      id: message.id ? `${message.role}-${message.id}` : `${message.role}-${Date.now()}`,
      serverMessageId: message.id,
      role: message.role,
      content:
        message.contents
          ?.map((content) => content.content)
          .join('\n')
          .trim() || '',
      createdAt: message.created_at,
    };
  }

  return null;
};

/**
 * yolo 会话的 history 含独立的 assistant 消息（分镜卡 / 视频附件 / 完成提示），
 * 必须各自渲染。任意一条 assistant 解析出 story（来自 type=yolo_segment）时即可
 * 判定为 yolo 会话；ShareChat / CaseDialog 拿不到 session_mode，借此从消息体
 * 推断也是稳的。
 */
export const isYoloLikeMessageList = (items: ChatMessageItem[]): boolean =>
  items.some((item) => item.role === 'assistant' && !!item.story);

export const mergeConsecutiveAssistantMessages = (items: ChatMessageItem[]): ChatMessageItem[] => {
  const result: ChatMessageItem[] = [];
  const turnIdIndexMap = new Map<string, number>();

  for (const item of items) {
    if (item.role === 'assistant') {
      if (item.story) {
        result.push(item);
        continue;
      }

      const targetIndex = item.turnId ? turnIdIndexMap.get(item.turnId) : undefined;

      if (targetIndex !== undefined) {
        const lastMessage = result[targetIndex];
        result[targetIndex] = {
          ...lastMessage,
          content: [lastMessage.content, item.content].filter(Boolean).join('\n\n').trim(),
          thinking: [lastMessage.thinking, item.thinking].filter(Boolean).join('\n\n').trim() || undefined,
          serverMessageId: item.serverMessageId || lastMessage.serverMessageId,
          shareMessageIds: mergeMessageIds(lastMessage.shareMessageIds, item.shareMessageIds),
          streamSeqs: mergeStreamSeqs(lastMessage.streamSeqs, item.streamSeqs),
          tool_calls: mergeToolCallsById(lastMessage.tool_calls ?? [], item.tool_calls ?? []),
          fileList: mergeAttachmentContentsByTypeAndContent(lastMessage.fileList ?? [], item.fileList ?? []),
          createdAt: item.createdAt || lastMessage.createdAt,
        };
      } else {
        const newIndex = result.length;
        result.push(item);
        if (item.turnId) {
          turnIdIndexMap.set(item.turnId, newIndex);
        }
      }
    } else {
      result.push(item);
    }
  }

  return result;
};

const extractFailedStateMessage = (contents?: ChatStreamContentRaw[], fallbackMessage = ''): string => {
  if (!contents?.length) return '';

  // turn.failed 有两种落地形态：
  //   1) type==='state' && status==='failed'（流式状态帧）
  //   2) type==='text' && status==='failed'（response_timeout 等把 JSON 错误体写进文本）
  // 两者都应折叠成本地化的失败提示，不暴露原始错误 JSON。
  const hasFailedState = contents.some((content) => content.status === 'failed');
  return hasFailedState ? fallbackMessage : '';
};

export function normalizePath(path?: string): string {
  return path?.trim() ?? '';
}

/**
 * 拿到签名 URL 后触发浏览器下载：新标签页打开，禁用 opener 防止反向控制。
 * claw 内所有「下载远端文件」的落地动作都收敛到这里，避免各处重复 window.open。
 */
export function openDownloadUrl(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export const SUBMIT_ERROR_REASON_ALIAS: Record<string, string> = {
  CreditsInsufficient: 'CreditInsufficient',
  InsufficientCredits: 'CreditInsufficient',
  NotEnoughCredits: 'CreditInsufficient',
  NoPermission: 'PermissionDenied',
  PermissionDenied: 'PermissionDenied',
  Forbidden: 'Forbidden',
  Unauthenticated: 'Unauthorized',
  Unauthorized: 'Unauthorized',
  RateLimitExceeded: 'TooManyRequests',
  TooManyRequests: 'TooManyRequests',
  Internal: 'InternalError',
  InternalError: 'InternalError',
};

/**
 * 把后端 _Error.reason 映射成 i18n key 后缀，供调用方配合 t() 渲染。
 * 返回完整的翻译字符串，需传入 t / t.has 以及当前 session 相关上下文。
 */
export const buildSubmitErrorMessage = (
  error: { reason?: string; message?: string } | null | undefined,
  {
    t,
    expectedFolder,
  }: {
    t: ((key: string, values?: Record<string, string>) => string) & { has: (key: string) => boolean };
    expectedFolder: string;
  },
): string => {
  const rawReason = error?.reason || (/^[A-Za-z][A-Za-z0-9_]*$/.test(error?.message ?? '') ? error?.message : '');
  const reason = rawReason ? (SUBMIT_ERROR_REASON_ALIAS[rawReason] ?? rawReason) : '';
  const reasonKey = reason ? `claw.chat.submitError.${reason}` : '';
  if (reasonKey && t.has(reasonKey)) {
    return t(reasonKey, { folder: expectedFolder });
  }
  return t('claw.chat.submitError.default');
};

export * from './coordinator';
export * from './media-browser';

export * from './story-payload';
